import { t } from 'ember-intl';
import { PhFilePdf } from 'ember-phosphor-icons';
import type { TOC } from '@ember/component/template-only';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';

/** One prior-step document plus the translation key naming the step it came from. */
export interface PriorUploadRow {
  asset: TasAssetModel;
  /** `null` for an unrecognized classification — the label is then omitted. */
  labelKey: string | null;
}

interface PriorUploadsListSignature {
  Args: {
    /** Live uploads from earlier approval steps. Empty → render nothing. */
    rows: PriorUploadRow[];
  };
  Element: HTMLDivElement;
}

/**
 * Displays live uploads from earlier approval steps. Approved evidence is view-only: it cannot be
 * discarded, reclassified, or counted toward current completion requirements.
 */
const PriorUploadsList: TOC<PriorUploadsListSignature> = <template>
  {{#if @rows.length}}
    <div class="tio-col-extra-tight" data-test-prior-uploads ...attributes>
      <h4 class="tio-text-sm-semibold uppercase tracking-wider tio-fg-muted">
        {{t "tas.attachments.previous_uploads"}}
      </h4>
      <p class="text-xs tio-fg-muted">
        {{t "tas.attachments.previous_uploads_hint"}}
      </p>
      <div class="tio-col-extra-tight">
        {{#each @rows as |row|}}
          {{! History rows use lighter styling; filenames retain the available width. }}
          <div
            class="tio-row-between flex-wrap gap-x-3 gap-y-1 rounded border tio-border-default tio-bg-surface px-3 py-2.5"
            data-test-prior-upload
          >
            <a
              href={{row.asset.signedUrl}}
              target="_blank"
              rel="noopener noreferrer"
              {{! A minimum filename width forces the fixed classification label to wrap on narrow screens. }}
              class="tio-inline-row gap-2 flex-1 min-w-32 tio-fg-secondary hover:tio-fg-info text-sm"
              title={{row.asset.filename}}
              data-test-prior-upload-link
            >
              <PhFilePdf @size="18" class="shrink-0" />
              <span class="truncate">{{row.asset.filename}}</span>
            </a>
            {{#if row.labelKey}}
              <span class="shrink-0 text-xs tio-fg-muted" data-test-prior-upload-classification>
                {{t row.labelKey}}
              </span>
            {{/if}}
          </div>
        {{/each}}
      </div>
    </div>
  {{/if}}
</template>;

export default PriorUploadsList;
